import React, { useState, useEffect, useCallback } from 'react';
import { utils } from '../../../utils';
import { usePermissions } from '../../../hooks';
import { SystemModule } from '../../../types/permissions';
import {
  listarTiposOcorrencia,
  criarTipoOcorrencia,
  atualizarTipoOcorrencia,
  deletarTipoOcorrencia,
  TipoOcorrenciaDivergencia,
  TipoAutomatico
} from '../../../services/tiposOcorrenciaDivergencia';

const TIPO_AUTOMATICO_LABELS: Record<TipoAutomatico, string> = {
  erro_conferente: 'Erro do Conferente',
  duplicata_cliente: 'Produto Duplicado',
  palete_nao_utilizado: 'Palete Não Utilizado',
};

const TiposOcorrenciaList: React.FC = () => {
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [tipos, setTipos] = useState<TipoOcorrenciaDivergencia[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTipo, setEditingTipo] = useState<TipoOcorrenciaDivergencia | null>(null);
  const [formData, setFormData] = useState({
    nome: '',
    codigo: '',
    descricao: '',
    ativo: true,
    automatico: false,
    tipo_automatico: null as TipoAutomatico | null,
    ocorrencia_de_corte: false,
  });

  const fetchTipos = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await listarTiposOcorrencia();
      if (result.success) {
        setTipos(result.tipos);
      }
    } catch (error: any) {
      utils.showNotification(error.message || 'Erro ao carregar tipos de ocorrência', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTipos();
  }, [fetchTipos]);

  // Verificar quais tipos automáticos já existem (excluindo o que está sendo editado)
  const tiposAutomaticosExistentes = tipos
    .filter(t => t.automatico && t.tipo_automatico && (!editingTipo || t.id !== editingTipo.id))
    .map(t => t.tipo_automatico as TipoAutomatico);

  const handleOpenModal = (tipo?: TipoOcorrenciaDivergencia) => {
    if (tipo) {
      setEditingTipo(tipo);
      setFormData({
        nome: tipo.nome,
        codigo: tipo.codigo,
        descricao: tipo.descricao || '',
        ativo: tipo.ativo,
        automatico: tipo.automatico,
        tipo_automatico: tipo.tipo_automatico || null,
        ocorrencia_de_corte: tipo.ocorrencia_de_corte || false,
      });
    } else {
      setEditingTipo(null);
      setFormData({ nome: '', codigo: '', descricao: '', ativo: true, automatico: false, tipo_automatico: null, ocorrencia_de_corte: false });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTipo(null);
    setFormData({ nome: '', codigo: '', descricao: '', ativo: true, automatico: false, tipo_automatico: null, ocorrencia_de_corte: false });
  };

  const generateCodigo = (nome: string): string => {
    return nome
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, '')
      .trim()
      .replace(/\s+/g, '_');
  };

  const handleNomeChange = (nome: string) => {
    setFormData(prev => ({
      ...prev,
      nome,
      codigo: editingTipo ? prev.codigo : generateCodigo(nome)
    }));
  };

  const handleAutomaticoChange = (checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      automatico: checked,
      tipo_automatico: checked ? prev.tipo_automatico : null,
      ocorrencia_de_corte: checked ? false : prev.ocorrencia_de_corte,
    }));
  };

  const handleSave = async () => {
    if (!formData.nome.trim() || !formData.codigo.trim()) {
      utils.showNotification('Nome e código são obrigatórios', { type: 'error' });
      return;
    }

    if (formData.automatico && !formData.tipo_automatico) {
      utils.showNotification('Selecione o tipo de ocorrência automática', { type: 'error' });
      return;
    }

    try {
      const payload = {
        nome: formData.nome,
        codigo: formData.codigo,
        descricao: formData.descricao,
        ativo: formData.ativo,
        automatico: formData.automatico,
        tipo_automatico: formData.automatico ? formData.tipo_automatico : null,
        ocorrencia_de_corte: formData.ocorrencia_de_corte,
      };

      if (editingTipo) {
        const result = await atualizarTipoOcorrencia(editingTipo.id, payload);
        if (result.success) {
          utils.showNotification('Tipo de ocorrência atualizado com sucesso!', { type: 'success' });
        }
      } else {
        const result = await criarTipoOcorrencia(payload);
        if (result.success) {
          utils.showNotification('Tipo de ocorrência criado com sucesso!', { type: 'success' });
        }
      }
      handleCloseModal();
      fetchTipos();
    } catch (error: any) {
      utils.showNotification(error.message || 'Erro ao salvar tipo de ocorrência', { type: 'error' });
    }
  };

  const handleDelete = async (id: number) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Tem certeza que deseja excluir este tipo de ocorrência?')) return;

    try {
      const result = await deletarTipoOcorrencia(id);
      if (result.success) {
        utils.showNotification('Tipo de ocorrência excluído com sucesso!', { type: 'success' });
        fetchTipos();
      }
    } catch (error: any) {
      utils.showNotification(error.message || 'Erro ao excluir tipo de ocorrência', { type: 'error' });
    }
  };

  const handleToggleAtivo = async (tipo: TipoOcorrenciaDivergencia) => {
    try {
      const result = await atualizarTipoOcorrencia(tipo.id, { ativo: !tipo.ativo });
      if (result.success) {
        utils.showNotification(
          `Tipo "${tipo.nome}" ${!tipo.ativo ? 'ativado' : 'desativado'} com sucesso!`,
          { type: 'success' }
        );
        fetchTipos();
      }
    } catch (error: any) {
      utils.showNotification(error.message || 'Erro ao atualizar status', { type: 'error' });
    }
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1><i className="fas fa-tags"></i> Ocorrências</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando tipos de ocorrência...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1><i className="fas fa-tags"></i> Ocorrências</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          {canCreate(SystemModule.CONFERENCIA_DIVERGENCIAS) && (
            <button className="btn btn-primary" onClick={() => handleOpenModal()}>
              <i className="fas fa-plus"></i>
              Novo Tipo
            </button>
          )}
        </div>
      </div>

      <div className="page-content">
        <div className="card">
          <div className="card-header">
            <h2>Tipos de ocorrência cadastrados</h2>
          </div>
          <div className="card-body">
            <p style={{ color: '#7f8c8d', fontSize: '0.9rem', marginBottom: 16 }}>
              Gerencie os tipos de ocorrência disponíveis para seleção ao registrar divergências na conferência de pré-carregamento.
            </p>

            {tipos.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-tags empty-state-icon"></i>
                <h3>Nenhum tipo de ocorrência cadastrado</h3>
                <p>Cadastre o primeiro tipo de ocorrência para usar nas divergências.</p>
                {canCreate(SystemModule.CONFERENCIA_DIVERGENCIAS) && (
                  <button className="btn btn-primary" onClick={() => handleOpenModal()}>
                    Cadastrar Primeiro Tipo
                  </button>
                )}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Descrição</th>
                      <th>Tipo</th>
                      <th>Status</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tipos.map(tipo => (
                      <tr key={tipo.id} style={{ opacity: tipo.ativo ? 1 : 0.6 }}>
                        <td>
                          <strong>{tipo.nome}</strong>
                        </td>
                        <td style={{
                          fontSize: '0.85rem', color: '#555', maxWidth: 250,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {tipo.descricao
                            ? <span title={tipo.descricao}>{tipo.descricao}</span>
                            : <em style={{ color: '#bbb' }}>Sem descrição</em>}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {tipo.automatico ? (
                              <span style={{
                                fontSize: '0.75rem', fontWeight: 600,
                                padding: '3px 8px', borderRadius: 10,
                                background: '#e8f5e9', color: '#2e7d32',
                                whiteSpace: 'nowrap',
                              }}>
                                <i className="fas fa-robot" style={{ marginRight: 4 }}></i>
                                {tipo.tipo_automatico ? TIPO_AUTOMATICO_LABELS[tipo.tipo_automatico] : 'Automática'}
                              </span>
                            ) : (
                              <span style={{
                                fontSize: '0.75rem', fontWeight: 600,
                                padding: '3px 8px', borderRadius: 10,
                                background: '#f5f5f5', color: '#757575',
                                whiteSpace: 'nowrap',
                              }}>
                                Manual
                              </span>
                            )}
                            {tipo.ocorrencia_de_corte && (
                              <span style={{
                                fontSize: '0.75rem', fontWeight: 600,
                                padding: '3px 8px', borderRadius: 10,
                                background: '#fff3e0', color: '#e65100',
                                whiteSpace: 'nowrap',
                              }}>
                                <i className="fas fa-cut" style={{ marginRight: 4 }}></i>
                                Corte
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <span
                            className={`status-badge ${tipo.ativo ? 'status-active' : 'status-danger'}`}
                            style={{ cursor: canUpdate(SystemModule.CONFERENCIA_DIVERGENCIAS) ? 'pointer' : 'default' }}
                            onClick={() => canUpdate(SystemModule.CONFERENCIA_DIVERGENCIAS) && handleToggleAtivo(tipo)}
                          >
                            {tipo.ativo ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            {canUpdate(SystemModule.CONFERENCIA_DIVERGENCIAS) && (
                              <button
                                className="btn-edit"
                                onClick={() => handleOpenModal(tipo)}
                                title="Editar"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                            )}
                            {canDelete(SystemModule.CONFERENCIA_DIVERGENCIAS) && (
                              <button
                                className="btn-delete"
                                onClick={() => handleDelete(tipo.id)}
                                title="Excluir"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de criação/edição */}
      {showModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 1000, padding: 20,
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: 24,
            width: '100%', maxWidth: 500, maxHeight: '90vh', overflow: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0 }}>
                <i className={`fas fa-${editingTipo ? 'edit' : 'plus'}`} style={{ marginRight: 8 }}></i>
                {editingTipo ? 'Editar Tipo de Ocorrência' : 'Novo Tipo de Ocorrência'}
              </h3>
              <button
                onClick={handleCloseModal}
                style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#7f8c8d' }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: '0.9rem' }}>Nome *</label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={e => handleNomeChange(e.target.value)}
                  placeholder="Ex: Volume faltante"
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.95rem' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: '0.9rem' }}>Código *</label>
                <input
                  type="text"
                  value={formData.codigo}
                  onChange={e => setFormData(prev => ({ ...prev, codigo: e.target.value }))}
                  placeholder="Ex: volume_faltante"
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.95rem', fontFamily: 'monospace' }}
                />
                <small style={{ color: '#7f8c8d' }}>Identificador único, gerado automaticamente a partir do nome</small>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: '0.9rem' }}>Descrição</label>
                <textarea
                  value={formData.descricao}
                  onChange={e => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
                  placeholder="Descrição opcional do tipo de ocorrência..."
                  rows={3}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.95rem', resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>

              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.ativo}
                    onChange={e => setFormData(prev => ({ ...prev, ativo: e.target.checked }))}
                  />
                  <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Ativo</span>
                </label>
              </div>

              {/* Seção de Ocorrência Automática */}
              <div style={{
                borderTop: '1px solid #eee',
                paddingTop: 16,
              }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 8 }}>
                  <input
                    type="checkbox"
                    checked={formData.automatico}
                    onChange={e => handleAutomaticoChange(e.target.checked)}
                  />
                  <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                    <i className="fas fa-robot" style={{ marginRight: 4, color: '#2e7d32' }}></i>
                    Ocorrência automática
                  </span>
                </label>
                <small style={{ color: '#7f8c8d', display: 'block', marginBottom: formData.automatico ? 12 : 0 }}>
                  Ocorrências automáticas são utilizadas pelo sistema para registrar divergências automaticamente durante a conferência.
                </small>

                {formData.automatico && (
                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: '0.9rem' }}>
                      Tipo de ocorrência automática *
                    </label>
                    <select
                      value={formData.tipo_automatico || ''}
                      onChange={e => setFormData(prev => ({ ...prev, tipo_automatico: e.target.value ? e.target.value as TipoAutomatico : null }))}
                      style={{
                        width: '100%', padding: '10px 12px', border: '1px solid #ddd',
                        borderRadius: 6, fontSize: '0.95rem', background: '#fff',
                      }}
                    >
                      <option value="">Selecione o tipo...</option>
                      {(Object.keys(TIPO_AUTOMATICO_LABELS) as TipoAutomatico[]).map(key => {
                        const jaExiste = tiposAutomaticosExistentes.includes(key);
                        return (
                          <option key={key} value={key} disabled={jaExiste}>
                            {TIPO_AUTOMATICO_LABELS[key]}{jaExiste ? ' (já cadastrada)' : ''}
                          </option>
                        );
                      })}
                    </select>
                    {formData.tipo_automatico && (
                      <small style={{
                        display: 'block', marginTop: 6,
                        color: '#1565c0', fontSize: '0.8rem',
                      }}>
                        <i className="fas fa-info-circle" style={{ marginRight: 4 }}></i>
                        {formData.tipo_automatico === 'erro_conferente'
                          ? 'Usada quando o conferente bipa a mesma etiqueta duas vezes por engano.'
                          : formData.tipo_automatico === 'duplicata_cliente'
                          ? 'Usada quando o cliente enviou o mesmo produto com etiqueta duplicada.'
                          : 'Usada quando um palete não recebeu nenhuma etiqueta e foi inutilizado automaticamente ao abrir uma nova montagem de carga.'}
                      </small>
                    )}
                  </div>
                )}
              </div>

              {/* Seção de Ocorrência de Corte */}
              <div style={{
                borderTop: '1px solid #eee',
                paddingTop: 16,
              }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 8 }}>
                  <input
                    type="checkbox"
                    checked={formData.ocorrencia_de_corte}
                    disabled={formData.automatico}
                    onChange={e => setFormData(prev => ({ ...prev, ocorrencia_de_corte: e.target.checked }))}
                  />
                  <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                    <i className="fas fa-cut" style={{ marginRight: 4, color: '#e65100' }}></i>
                    Ocorrência de Corte
                  </span>
                </label>
                <small style={{ color: '#7f8c8d', display: 'block' }}>
                  Quando marcado, esta ocorrência indica um ponto de corte de pallet.
                  O pedido será dividido entre múltiplos pallets, mantendo apenas as etiquetas já bipadas
                  no pallet atual e gerando somente as restantes em novos pallets.
                </small>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={handleCloseModal}
                style={{ padding: '10px 20px' }}
              >
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={!formData.nome.trim() || !formData.codigo.trim() || (formData.automatico && !formData.tipo_automatico)}
                style={{ padding: '10px 20px' }}
              >
                <i className={`fas fa-${editingTipo ? 'save' : 'plus'}`} style={{ marginRight: 6 }}></i>
                {editingTipo ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TiposOcorrenciaList;
